/**
 * Etiqueta de LOTE: o QR carrega o `codLot`, e o texto legível traz o lote
 * em letra grande e, menores, o produto, a derivação e a validade.
 *
 * O QR e a posição dele são os mesmos da etiqueta de produto (`etiqueta.ts`):
 * versão 2-H, módulo e margem branca iguais, à mesma distância do topo. Só a
 * faixa de texto abaixo da margem muda.
 */
import type { Lote } from '../../api/lotes.js';
import { formatarDataCivil } from '../../componentes/datas.js';
import { rotuloDoLocal } from '../../componentes/deposito.js';
import type { TamanhoDeEtiqueta } from './etiqueta.js';
import { AVANCO_DA_FONTE_MONO_EM, COPIAS_MAXIMAS, layoutDaEtiqueta } from './etiqueta.js';

/**
 * Capacidade da versão 2 com correção H, por modo de codificação. O mesmo
 * teto que o middleware aplica antes de responder `LOTE_NAO_CABE_NO_QR`.
 */
export const CAPACIDADE_DO_QR = { numerico: 34, alfanumerico: 20, byte: 14 } as const;

/** Teto de lotes numa impressão: cada um é uma ida ao middleware. */
export const LIMITE_DE_LOTES = 50;

/** Corpo do detalhe em fração do corpo do código, antes de caber na largura. */
const PROPORCAO_DO_DETALHE = 0.4;

/** Entrelinha das linhas de detalhe, em fração do corpo delas. */
const ENTRELINHA = 1.3;

const ASCII_IMPRIMIVEL = /^[\x20-\x7e]+$/u;
const SO_DIGITOS = /^\d+$/u;
const ALFANUMERICO_DO_QR = /^[0-9A-Z $%*+\-./:]+$/u;

function capacidadePara(codLot: string): number {
  if (SO_DIGITOS.test(codLot)) {
    return CAPACIDADE_DO_QR.numerico;
  }
  if (ALFANUMERICO_DO_QR.test(codLot)) {
    return CAPACIDADE_DO_QR.alfanumerico;
  }
  return CAPACIDADE_DO_QR.byte;
}

/**
 * Motivo da recusa do `codLot`, ou `null` quando ele cabe no QR.
 *
 * Recusar aqui diz o motivo antes de perguntar ao middleware; a resposta
 * `LOTE_NAO_CABE_NO_QR` dele continua tratada, para o caso de as regras
 * divergirem.
 */
export function validarCodLot(codLot: string): string | null {
  if (codLot.length === 0) {
    return 'Código de lote vazio.';
  }
  if (!ASCII_IMPRIMIVEL.test(codLot)) {
    return 'Caractere fora do padrão: só ASCII imprimível, sem acento.';
  }
  if (codLot !== codLot.trim()) {
    return 'Espaço no começo ou no fim.';
  }

  const capacidade = capacidadePara(codLot);
  return codLot.length > capacidade
    ? `O código do lote não cabe no QR da etiqueta (${String(codLot.length)} caracteres; aqui cabem ${String(capacidade)}).`
    : null;
}

/** Uma etiqueta por produto + derivação + lote, venha de quantos depósitos vier. */
export function chaveDoLote(lote: Pick<Lote, 'codPro' | 'codDer' | 'codLot'>): string {
  return JSON.stringify([lote.codPro, lote.codDer, lote.codLot]);
}

export type LoteDaEtiqueta = {
  readonly chave: string;
  readonly lote: Lote;
  /** Onde o lote tem saldo: `Filial 1 · ALM01`. */
  readonly locais: readonly string[];
};

/**
 * A busca devolve uma linha por depósito; a etiqueta é do lote. O mesmo lote
 * do mesmo produto em dois depósitos vira uma linha só, com os dois locais.
 */
export function lotesPorEtiqueta(itens: readonly Lote[]): readonly LoteDaEtiqueta[] {
  return itens.reduce<readonly LoteDaEtiqueta[]>((agrupados, lote) => {
    const chave = chaveDoLote(lote);
    const local = rotuloDoLocal(lote.codFil, lote.codDep);
    const existente = agrupados.find((item) => item.chave === chave);

    if (existente === undefined) {
      return [...agrupados, { chave, lote, locais: [local] }];
    }

    return agrupados.map((item) =>
      item === existente && !item.locais.includes(local)
        ? { ...item, locais: [...item.locais, local] }
        : item,
    );
  }, []);
}

/** As linhas menores abaixo do `codLot`. */
export function linhasDeDetalhe(lote: Pick<Lote, 'codPro' | 'codDer' | 'datVlt'>): string[] {
  const produto =
    lote.codDer.trim() === ''
      ? `Prod. ${lote.codPro}`
      : `Prod. ${lote.codPro} · Der. ${lote.codDer}`;
  const validade = formatarDataCivil(lote.datVlt, '');

  return validade === '' ? [produto] : [produto, `Validade ${validade}`];
}

export type TextoDoLote = {
  readonly centroMm: number;
  readonly lote: { readonly linhaDeBaseMm: number; readonly fonteMm: number };
  readonly detalhe: { readonly linhasDeBaseMm: readonly number[]; readonly fonteMm: number };
};

function mm(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

/**
 * Onde vai cada linha de texto, em milímetros.
 *
 * O corpo do `codLot` é o do tamanho (o mesmo do `codPro` na etiqueta de
 * produto) e encolhe só o necessário para caber na largura; o detalhe é
 * menor. O bloco fica centralizado na faixa entre a margem branca e a borda
 * de baixo — e, se não couber nela, as duas letras encolhem juntas.
 */
export function layoutDoTextoDoLote(
  tamanho: TamanhoDeEtiqueta,
  codLot: string,
  detalhes: readonly string[],
): TextoDoLote {
  const base = layoutDaEtiqueta(tamanho);
  const larguraUtil = tamanho.larguraMm - 2 * tamanho.topoMm;
  const maiorDetalhe = Math.max(1, ...detalhes.map((linha) => linha.length));

  const fonteDoLote = Math.min(
    tamanho.fonteMm,
    larguraUtil / (Math.max(1, codLot.length) * AVANCO_DA_FONTE_MONO_EM),
  );
  const fonteDoDetalhe = Math.min(
    tamanho.fonteMm * PROPORCAO_DO_DETALHE,
    larguraUtil / (maiorDetalhe * AVANCO_DA_FONTE_MONO_EM),
  );

  const topoDaFaixa = base.texto.topoMm;
  const faixa = tamanho.alturaMm - tamanho.topoMm - topoDaFaixa;
  const alturaDoBloco = fonteDoLote + detalhes.length * fonteDoDetalhe * ENTRELINHA;
  const escala = Math.min(1, faixa / alturaDoBloco);

  const lote = fonteDoLote * escala;
  const detalhe = fonteDoDetalhe * escala;
  const topo = topoDaFaixa + (faixa - alturaDoBloco * escala) / 2;
  const linhaDoLote = topo + lote;

  return {
    centroMm: base.texto.centroMm,
    lote: { linhaDeBaseMm: mm(linhaDoLote), fonteMm: mm(lote) },
    detalhe: {
      fonteMm: mm(detalhe),
      linhasDeBaseMm: detalhes.map((_, indice) =>
        mm(linhaDoLote + (indice + 1) * detalhe * ENTRELINHA),
      ),
    },
  };
}

export type EtiquetaDeLoteParaImprimir = { readonly chave: string; readonly chaveDoLote: string };

/** Cada lote repetido pelas cópias, na ordem da lista. */
export function etiquetasDeLoteParaImprimir(
  chaves: readonly string[],
  copias: number,
): readonly EtiquetaDeLoteParaImprimir[] {
  const vezes = Math.min(COPIAS_MAXIMAS, Math.max(1, Math.trunc(copias)));

  return chaves.flatMap((chaveDoLote) =>
    Array.from({ length: vezes }, (_, copia) => ({
      chave: `${chaveDoLote}#${String(copia)}`,
      chaveDoLote,
    })),
  );
}
